/**
 * NINVerifyPage — `/mail/nin-verify`.
 *
 * Ported from `screens/src/pages/mail/NINVerify.jsx`. Inline NIN check during
 * counter parcel registration. Calls service-kyc.
 *
 * Backend wiring (live):
 *   - POST /v1/kyc/verifications/nin
 */
import { type FormEvent, type JSX, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { verifyNin, type VerificationResponse } from '../../lib/api/kyc';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

export function NINVerifyPage(): JSX.Element {
  const [nin, setNin] = useState<string>('');
  const [fullNameHint, setFullNameHint] = useState<string>('');
  const [result, setResult] = useState<VerificationResponse | null>(null);

  const verify = useMutation({
    mutationFn: () =>
      verifyNin({
        nin: nin.trim(),
        ...(fullNameHint.trim() !== '' && { fullNameHint: fullNameHint.trim() }),
      }),
    onSuccess: (r) => setResult(r),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (nin.trim().length === 11) {
      verify.mutate();
    }
  }

  const denied = verify.isError && verify.error instanceof ApiError && (verify.error.status === 401 || verify.error.status === 403);
  const ninValid = /^\d{11}$/.test(nin.trim());

  return (
    <div className="space-y-5">
      <PageHeader
        title="NIN Verify"
        description="Inline National Identity Number verification at the counter."
      />

      <form onSubmit={handleSubmit} className="bg-white border border-border rounded-xl p-5 space-y-3">
        <div>
          <label htmlFor="nin-input" className="text-xs font-semibold text-muted uppercase">NIN (11 digits)</label>
          <input
            id="nin-input"
            type="text"
            inputMode="numeric"
            maxLength={11}
            value={nin}
            onChange={(e) => setNin(e.target.value.replace(/\D/g, ''))}
            placeholder="12345678901"
            aria-label="NIN"
            className="mt-1 w-full px-3 py-2 text-base font-mono border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nipost-green/20"
          />
          {nin.length > 0 && !ninValid ? (
            <p className="text-xs text-amber-700 mt-1">NIN must be exactly 11 digits.</p>
          ) : null}
        </div>
        <div>
          <label htmlFor="nin-name" className="text-xs font-semibold text-muted uppercase">Name hint (optional)</label>
          <input
            id="nin-name"
            type="text"
            value={fullNameHint}
            onChange={(e) => setFullNameHint(e.target.value)}
            placeholder="As stated by the customer"
            className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg"
          />
        </div>
        <div className="flex items-center justify-end gap-3">
          {verify.isError && !denied ? (
            <span className="text-xs text-red-700">{(verify.error as Error).message}</span>
          ) : null}
          <button
            type="submit"
            disabled={!ninValid || verify.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            <ShieldCheck className="w-4 h-4" /> {verify.isPending ? 'Verifying…' : 'Verify NIN'}
          </button>
        </div>
      </form>

      {denied ? (
        <DeferredNotice title="Permission required">
          Your account does not have the KYC verification permission. Ask an administrator
          to grant the KYC runVerification role.
        </DeferredNotice>
      ) : null}

      {result ? (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-xs font-semibold text-muted uppercase tracking-widest">Result</p>
              <p className="font-mono text-base font-bold text-gray-900">{result.id}</p>
            </div>
            <StatusBadge status={result.status} />
          </div>
          <dl className="text-sm grid grid-cols-2 gap-2">
            <dt className="text-muted text-xs">Type</dt>
            <dd>{result.type}</dd>
            <dt className="text-muted text-xs">Subject</dt>
            <dd className="font-mono text-xs">{result.subjectId}</dd>
            {result.providerRef ? (
              <>
                <dt className="text-muted text-xs">Provider ref</dt>
                <dd className="font-mono text-xs">{result.providerRef}</dd>
              </>
            ) : null}
          </dl>
        </div>
      ) : null}
    </div>
  );
}
